import {
    getSortIndexFromUrlValue,
    ORDERED_FILTERS,
    ORDERED_SEO_FRIENDLY_FACET_FILTERS,
} from '@libs/algolia';
import { validateQueryFacet } from '@app/plp/utils/validateQueryFacets';
import {
    CATEGORY_SEPARATOR,
    FACETS_KEY,
    FEATURED_BRANDS_KEY,
    URL_ENCODED,
} from '../constants/facetedNavigation';
import type { Facets, MappedFacetsValue } from '../types/facetedNavigation';
import { internalTemporaryRedirect } from '@libs/common-next';
import urljoin from 'url-join';

const unformatBrandValue = (brandName: string) =>
    decodeURIComponent(brandName)
        .replaceAll('-', ' ')
        .replaceAll("'", '')
        .replaceAll(' and ', ' & ')
        .toLowerCase();

const formatCategoryUrl = (path: string) =>
    path.toLowerCase().replaceAll(CATEGORY_SEPARATOR, '/').replaceAll(' ', '-');

const findBrandMatch = (brandName: string, validBrands: string[]) =>
    validBrands
        ?.find(
            brand => unformatBrandValue(brand) === unformatBrandValue(brandName)
        )
        ?.toLowerCase();

/**
 * Attempt to redirect URLs using old format with a single brand filter applied.
 *
 * @returns
 * call to `redirect()` if a URL with a single brand filter in old format is detected.
 *
 * @example
 * - URL with multiple brand values **will not** redirect:
 *   - `/dog/food/wet-food/authority+pedigree`
 * - URL where brand is part of category path **will not** redirect:
 *   - `/featured-brands/authority`
 * - URL in old format with a single brand filter **will redirect** to new format:
 *   - `/dog/food/wet-food/authority` → `/dog/food/wet-food/f/brand/authority`
 * ```ts
 * ```
 */
const redirectIfBrandFacetMatch = (
    categoryPath: string,
    lastFacet: string | undefined,
    algoliaFilters: Record<string, string[]>,
    searchParams: Record<string, string | string[]> = {}
) => {
    /**
     * Skip redirect attempt if:
     * - last facet is undefined
     * - last facet contains multiple values to reduce complexity
     * - brand name is part of a valid category path
     */
    if (
        !lastFacet ||
        lastFacet.includes(URL_ENCODED.PLUS_SIGN) ||
        categoryPath.includes(FEATURED_BRANDS_KEY)
    ) {
        return undefined;
    }

    const brandName = findBrandMatch(lastFacet, algoliaFilters?.brand);

    if (brandName) {
        const formattedBrand = encodeURIComponent(brandName).replaceAll(
            "'",
            URL_ENCODED.APOSTROPHE
        );

        const queryParams = formatQueryParams(searchParams);

        const newPath = urljoin(
            formatCategoryUrl(categoryPath),
            FACETS_KEY,
            'brand',
            formattedBrand
        );

        console.info(`[PLP] Logical Redirect:
            "${categoryPath}" matched old format with single brand.
            Redirecting to "${newPath}"`);

        internalTemporaryRedirect(`/${newPath}${queryParams}`);
    }

    return undefined;
};

export const formatQueryParams = (
    searchParams: Record<string, string | string[]> = {}
) => {
    const queryParams = new URLSearchParams(
        searchParams as Record<string, string>
    ).toString();
    return queryParams ? `?${queryParams}` : '';
};

/**
 * Attempt to redirect URLs using old format.
 *
 * @returns
 * call to `redirect()` if a URL with facet filers in old format is detected.
 *
 * @example
 * - URL with facets in new format **will not** redirect:
 *   - `/dog/food/wet-food/f/brand/authority/flavor/chicken`
 * - URL in old format with a single brand filter **will redirect** to new format:
 *   - `/dog/food/wet-food/authority` → `/dog/food/wet-food/f/brand/authority`
 * - URL in old format with a multiple filters **will redirect** to closest category:
 *   - `/dog/food/wet-food/authority/chicken` → `/dog/food/wet-food`
 * - URL in old format that does not match a valid category path  **will not** redirect:
 *   - `/an-unknown/category/with/filters`
 * ```ts
 * ```
 *
 * @see {@link https://petsmart.atlassian.net/browse/HCP-703 | HCP-703} for details on 1-1 redirects.
 */
export const doRedirectForOldFormatUrls = (
    algoliaFilters: Record<string, string[]>,
    slugs: string[],
    searchParams: Record<string, string | string[]> = {},
    matchedCategoryPath = ''
) => {
    const isNewFacetFormatUrl = slugs?.indexOf(FACETS_KEY) >= 0;

    // Skip if facets are already in new format
    if (isNewFacetFormatUrl) {
        return undefined;
    }

    if (matchedCategoryPath) {
        // We cannot guess what all filters in the old format are,
        // but we will at least try to apply single brand filters
        redirectIfBrandFacetMatch(
            matchedCategoryPath,
            slugs.at(-1),
            algoliaFilters,
            searchParams
        );

        console.info(`[PLP] Logical Redirect:
            "${slugs.join('/')}" did not match a valid category.
            Redirecting to "${formatCategoryUrl(matchedCategoryPath)}"`);

        const queryParams = formatQueryParams(searchParams);
        internalTemporaryRedirect(
            `/${formatCategoryUrl(matchedCategoryPath)}${queryParams}`
        );
    }

    return undefined;
};

export const checkUrlFormat = (
    slugs: string[] = [],
    searchParams: Record<string, string | string[]> = {}
) => {
    // Skip if no facets in new format are detected
    if (!slugs.includes(FACETS_KEY)) {
        return;
    }

    const currentPath = decodeURIComponent(slugs.join('/'));
    const { categorySlugs, facetSlugs } = splitCategoryFromFacets(slugs);
    const sortedSlugs: Array<string> = [];

    // SEO friendly filters should always follow the same order,
    // and if multiple values are present, they should sorted alphabetically.
    Object.values(ORDERED_SEO_FRIENDLY_FACET_FILTERS).forEach(key => {
        const keyIndex = facetSlugs.findIndex(
            i => i.toLowerCase() === key.toLowerCase()
        );
        if (keyIndex >= 0 && facetSlugs[keyIndex + 1]) {
            sortedSlugs.push(
                key.toLocaleLowerCase(),
                facetSlugs[keyIndex + 1]
                    ?.split(URL_ENCODED.PLUS_SIGN)
                    .sort((a, b) => a.localeCompare(b))
                    .map(i => i.toLowerCase())
                    .join('+')
            );
        }
    });

    // Appended facets should all be lowercased
    const preferredUrl = `${categorySlugs.join(
        '/'
    )}/${FACETS_KEY}/${sortedSlugs.join('/')}`;

    if (currentPath !== decodeURIComponent(preferredUrl)) {
        const queryParams = formatQueryParams(searchParams);

        console.info(`[PLP] Logical Redirect:
            "${currentPath}" is improperly formatted.
            Redirecting to "${preferredUrl}"`);

        internalTemporaryRedirect(`/${preferredUrl}${queryParams}`);
    }
};

const URL_TO_REFINEMENT_MAP: Record<string, string> = {
    sort: 'sortingRule',
    price: 'price.number',
    rating: 'customBvAverageRating',
    q: 'query',
};

const formatFacetValue = (
    refinementId: string,
    value: string,
    algoliaFilters: Record<string, string[]>
) => {
    const matchedValue = algoliaFilters[refinementId]?.find(
        validValue =>
            validValue.toLowerCase() === decodeURIComponent(value).toLowerCase()
    );

    return matchedValue ?? formatUnmatchedValue(value);
};

const findRefinement = (keyUrlName: string) => {
    const mappedValue = URL_TO_REFINEMENT_MAP[keyUrlName];

    if (mappedValue) {
        return {
            refinementId: mappedValue,
            urlName: keyUrlName,
        };
    }

    const matchedItem = ORDERED_FILTERS.find(
        filter => filter.urlName.toLowerCase() === keyUrlName.toLowerCase()
    );

    return {
        refinementId: matchedItem?.attribute ?? keyUrlName,
        urlName: matchedItem?.urlName ?? keyUrlName,
    };
};

const mapSeoFriendlyFacets = (
    rawFacets: string[],
    algoliaFilters: Record<string, string[]>
) => {
    const facets: Facets[] = [];

    for (let index = 0; index < rawFacets.length; index += 2) {
        const key = rawFacets.at(index);
        const values = rawFacets.at(index + 1)?.split(URL_ENCODED.PLUS_SIGN);
        const { refinementId, urlName } = findRefinement(key ?? '');

        if (key && values && refinementId) {
            const formattedValues = values.map(value =>
                formatFacetValue(refinementId, value, algoliaFilters)
            );

            facets.push({ [urlName]: formattedValues });
        }
    }

    return facets;
};

const normalizeToArray = (value: string | string[]): string[] => {
    return Array.isArray(value) ? value : [value];
};

const mapQueryParamFacets = (
    searchParams: Record<string, string | string[]>,
    algoliaFilters: Record<string, string[]>
) => {
    const mappedFacets: Array<Record<string, string[] | string>> = [];

    Object.keys(searchParams).forEach(key => {
        const { refinementId } = findRefinement(key);
        const isNumeric = key === 'price' || key === 'rating';
        const isSearchQuery = key === 'q';

        const rawValues = normalizeToArray(searchParams[key]);

        const values = rawValues.map(decodeURIComponent);

        const validValues = values.filter(value =>
            validateQueryFacet(refinementId, value, algoliaFilters)
        );

        if (isNumeric || isSearchQuery) {
            mappedFacets.push({ [refinementId]: validValues.at(0) ?? '' });
        } else {
            mappedFacets.push({ [key]: validValues });
        }
    });

    return mappedFacets;
};

export const mapSortRule = (
    searchParams: Record<string, string | string[]>,
    isSearchPage: boolean
) => {
    // The “safe” fallback key for this context
    const defaultSortKey = isSearchPage ? 'relevance' : 'best-sellers';

    // What the user actually asked for (or the default if they gave us nothing)
    const requestedSortKey =
        typeof searchParams.sort === 'string' && searchParams.sort.trim()
            ? searchParams.sort
            : defaultSortKey;

    // Translate that key into a valid sort index.
    const sortIndex = getSortIndexFromUrlValue(requestedSortKey);
    // If it’s valid, we’re done
    if (sortIndex != null) {
        return sortIndex;
    }

    // Otherwise overwrite the param with our default and try again
    searchParams.sort = defaultSortKey;
    return getSortIndexFromUrlValue(defaultSortKey) ?? defaultSortKey;
};

export const formatCategoryPath = (path: string[]) =>
    path.map(item => item.replaceAll('-', ' ')).join(CATEGORY_SEPARATOR);

export const splitCategoryFromFacets = (slugs: string[] = []) => {
    if (!slugs.length) {
        return {
            categorySlugs: [],
            facetSlugs: [],
        };
    }

    const facetsIndex = slugs?.indexOf(FACETS_KEY);
    const hasFacets = facetsIndex >= 0;

    return {
        categorySlugs: hasFacets ? slugs.slice(0, facetsIndex) : slugs,
        facetSlugs: hasFacets ? slugs.slice(facetsIndex + 1) : [],
    };
};

export const mapUrlToFacetValues = (
    slugs: string[],
    searchParams: Record<string, string[] | string>,
    algoliaFilters: Record<string, string[]>,
    isSearchPage: boolean
): MappedFacetsValue => {
    const facets: Facets[] = [];

    const { categorySlugs, facetSlugs: rawFacets } =
        splitCategoryFromFacets(slugs);

    // Create category path
    const categoryPath = formatCategoryPath(categorySlugs) ?? '';

    // Map SEO friendly facets
    if (rawFacets.length) {
        const seoFriendlyFacets = mapSeoFriendlyFacets(
            rawFacets,
            algoliaFilters
        );

        facets.push(...seoFriendlyFacets);
    }

    // Map facets in query params
    if (Object.keys(searchParams).length) {
        const queryParamFacets = mapQueryParamFacets(
            searchParams,
            algoliaFilters
        );
        facets.push(...queryParamFacets);
    }

    // Map sorting rule
    facets.push({ sort: mapSortRule(searchParams, isSearchPage) });

    return {
        categoryPath: isSearchPage ? '' : categoryPath,
        facets,
    };
};

export const formatUnmatchedValue = (value: string) => {
    return decodeURIComponent(value)
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
